import { Pin, X, Building2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import FileTypeIcon from './FileTypeIcon';
import { formatRelative, daysUntil } from '../../utils/format';

/**
 * The documents somebody decided everybody should see.
 *
 * Read at a glance or not at all, which decides most of what is here and nearly all of what
 * is not. Each row carries the file's icon, its name, one line saying *why* it is pinned,
 * and where it applies. Size, owner, upload date and folder path are all available and all
 * omitted: they are one click away in the details panel, and here they would bury the one
 * line that earns the feature.
 *
 * The note is that line. "Renewed — use this one, not the 2025 copy" stops somebody sending
 * a client an expired licence; without it a pin is a favourite that somebody else chose for
 * you, and people ask why it is on their screen. When nobody wrote a note the row falls back
 * to who pinned it and when, so the question "says who?" always has an answer.
 *
 * Renders nothing at all when empty. A placeholder advertising an unused feature on
 * everybody's dashboard is worse than the empty space it fills.
 */
export default function PinnedBand({ pins = [], loading = false, onOpen, onUnpin, showViewAll = true }) {
  if (loading && pins.length === 0) return null;
  if (pins.length === 0) return null;

  return (
    <div className="panel panel-list">
      <div className="panel-head">
        <h3 className="row" style={{ gap: 7 }}>
          <Pin size={15} style={{ color: 'var(--ac)' }} />
          Pinned
        </h3>
        {showViewAll && (
          <Link to="/pinned" className="link">
            View all
          </Link>
        )}
      </div>

      {pins.map((p) => {
        const days = p.expiresAt ? daysUntil(p.expiresAt) : null;
        // Only mentioned when it is nearly due. A date three months out is noise; one three
        // days out is the reason somebody might act today.
        const leavingSoon = days !== null && days <= 7;

        return (
          <div
            key={p.id}
            className="trow"
            style={{ gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center' }}
            onClick={() => onOpen?.(p.document)}
          >
            <div className="cell-name">
              <FileTypeIcon name={p.document.name} />
              <div style={{ minWidth: 0 }}>
                <div className="title">{p.document.name}</div>
                <div className="sub">
                  {p.note || `${p.pinnedBy || 'Pinned'} · ${formatRelative(p.createdAt)}`}
                </div>
              </div>
            </div>

            <span className="row" style={{ gap: 6, flex: 'none' }}>
              {leavingSoon && (
                <span className="meta row" style={{ gap: 3, fontSize: 11 }} title={`Unpins ${new Date(p.expiresAt).toDateString()}`}>
                  <Clock size={10} />
                  {days <= 0 ? 'Unpins today' : `${days}d`}
                </span>
              )}

              {/* Which audience this is for, so nobody wonders why a document from another
                  department is on their dashboard. */}
              <span className="pill" title={p.scope === 'organization' ? 'Pinned for everyone' : `Pinned for ${p.department?.name}`}>
                {p.scope === 'organization' ? (
                  <>
                    <Building2 size={10} style={{ marginRight: 4, verticalAlign: -1 }} />
                    Company
                  </>
                ) : (
                  p.department?.name || 'Department'
                )}
              </span>

              {onUnpin && p.canRemove !== false && (
                <button
                  type="button"
                  className="btn--icon"
                  style={{ width: 26, height: 26 }}
                  aria-label={`Unpin ${p.document.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpin(p);
                  }}
                >
                  <X size={13} />
                </button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
